// src/pages/admin/AdminModeracion.js
//
// Vista admin para revisar mensajes bloqueados y alertas de moderación
// (D-19, Nivel 1). Read-only por ahora — futuras versiones incluirán
// suspensión de usuarios y aprendizaje de falsos positivos.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, FormControl, InputLabel, Select, MenuItem, Stack, Button, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import RefreshIcon      from '@mui/icons-material/Refresh';
import FlagIcon         from '@mui/icons-material/Flag';
import EditNoteIcon     from '@mui/icons-material/EditNote';
import { moderacion } from '../../api/authApi';
import { fechaHoraLocal } from '../../utils/format';

const SEVERIDADES = [
  { value: '',  label: 'Todas' },
  { value: 3,   label: 'Alta (amenazas)' },
  { value: 2,   label: 'Media (insultos graves)' },
  { value: 1,   label: 'Baja (groserías)' },
];

const TIPOS_CHAT = [
  { value: '',              label: 'Todos' },
  { value: 'VECI_TENDERO',  label: 'Veci ↔ Tendero' },
  { value: 'DOMI_NEGOCIO',  label: 'Domiciliario ↔ Negocio' },
  { value: 'RECOMENDACION_TIENDA', label: 'Tienda recomendada (veci)' },
];

const colorSeveridad = (s) => s === 3 ? 'error' : s === 2 ? 'warning' : 'default';
const nombreSeveridad = (s) => s === 3 ? 'Alta' : s === 2 ? 'Media' : 'Baja';

/**
 * Muestra los datos del participante para reporte a autoridad:
 * nombre completo, cédula (o NIT), teléfono. Puede recibir:
 * - `datos`: veci, tendero (negocio), o domiciliario resuelto por el backend
 * - `identity`: user Identity (email adicional)
 * - `rol`: etiqueta del rol dentro del chat
 */
const DatosParticipante = ({ datos, identity, rol }) => {
  if (!datos && !identity) return <span style={{ color: '#999' }}>—</span>;
  // Negocio (tiene razonSocial/nit) vs persona (nombreCompleto/documento)
  const esNegocio = datos && (datos.razonSocial || datos.nit);
  return (
    <div>
      {rol && (
        <Chip size="small" label={rol} sx={{ mb: 0.5, height: 18, fontSize: 10 }} />
      )}
      {esNegocio ? (
        <>
          <div><b>{datos.nombreComercial || datos.razonSocial}</b></div>
          {datos.razonSocial && datos.razonSocial !== datos.nombreComercial && (
            <div style={{ fontSize: 11, color: '#666' }}>{datos.razonSocial}</div>
          )}
          {datos.nit && <div style={{ fontSize: 11 }}>NIT: {datos.nit}</div>}
        </>
      ) : datos ? (
        <>
          <div><b>{datos.nombreCompleto || '(sin nombre)'}</b></div>
          {datos.documento && <div style={{ fontSize: 11 }}>CC: {datos.documento}</div>}
          {datos.telefono && <div style={{ fontSize: 11 }}>Tel: {datos.telefono}</div>}
        </>
      ) : null}
      {identity?.email && (
        <div style={{ fontSize: 11, color: '#666' }}>{identity.email}</div>
      )}
    </div>
  );
};

export default function AdminModeracion() {
  const [severidad, setSeveridad]         = useState('');
  const [tipoChat, setTipoChat]           = useState('');
  const [soloBloqueados, setSoloBloqueados] = useState(false);
  const [items, setItems]                 = useState([]);
  const [contadores, setContadores]       = useState([]);
  const [cargando, setCargando]           = useState(false);
  const [error, setError]                 = useState(null);
  const [notasDialog, setNotasDialog]     = useState(null);
  const [notasTexto, setNotasTexto]       = useState('');

  const cargar = useCallback(async () => {
    setCargando(true); setError(null);
    try {
      const r = await moderacion.listar({
        severidad: severidad || undefined,
        tipoChat:  tipoChat  || undefined,
        soloBloqueados,
        desdeDias: 30,
      });
      setItems(r.items || []);
      setContadores(r.contadores || []);
    } catch (e) {
      setError(e?.message || 'No se pudo cargar la lista.');
    } finally {
      setCargando(false);
    }
  }, [severidad, tipoChat, soloBloqueados]);

  useEffect(() => { cargar(); }, [cargar]);

  const marcarFalsoPositivo = async (id) => {
    if (!window.confirm('¿Marcar este intento como falso positivo? Ayudará a mejorar el filtro en futuras versiones.')) return;
    try {
      await moderacion.marcarFalsoPositivo(id);
      cargar();
    } catch (e) { alert(e?.message || 'No se pudo marcar.'); }
  };

  const abrirNotas = (item) => {
    setNotasDialog(item);
    setNotasTexto(item.notasAdmin || '');
  };

  const guardarNotas = async () => {
    try {
      await moderacion.guardarNotas(notasDialog.id, notasTexto);
      setNotasDialog(null); setNotasTexto('');
      cargar();
    } catch (e) { alert(e?.message || 'No se pudieron guardar las notas.'); }
  };

  const totalPorSeveridad = (s) =>
    contadores.find(c => c.severidad === s)?.total || 0;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Moderación de mensajes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Últimos 30 días. Nivel 1 — lista de palabras con severidad.
        Los intentos bloqueados nunca llegaron al destinatario.
      </Typography>

      {/* Contadores */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Paper elevation={0} sx={{ p: 2, minWidth: 140, border: '1px solid', borderColor: 'error.light' }}>
          <Typography variant="caption" color="text.secondary">Amenazas (alta)</Typography>
          <Typography variant="h4" color="error">{totalPorSeveridad(3)}</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 2, minWidth: 140, border: '1px solid', borderColor: 'warning.light' }}>
          <Typography variant="caption" color="text.secondary">Insultos graves (media)</Typography>
          <Typography variant="h4" color="warning.main">{totalPorSeveridad(2)}</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 2, minWidth: 140, border: '1px solid', borderColor: 'grey.400' }}>
          <Typography variant="caption" color="text.secondary">Groserías (baja)</Typography>
          <Typography variant="h4">{totalPorSeveridad(1)}</Typography>
        </Paper>
      </Stack>

      {/* Filtros */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Severidad</InputLabel>
          <Select value={severidad} label="Severidad"
            onChange={(e) => setSeveridad(e.target.value)}>
            {SEVERIDADES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Tipo de chat</InputLabel>
          <Select value={tipoChat} label="Tipo de chat"
            onChange={(e) => setTipoChat(e.target.value)}>
            {TIPOS_CHAT.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Button
          variant={soloBloqueados ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setSoloBloqueados(v => !v)}
        >
          Solo bloqueados
        </Button>
        <Button startIcon={<RefreshIcon />} size="small" onClick={cargar}>
          Actualizar
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Sev.</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Remitente</TableCell>
              <TableCell>Destinatario</TableCell>
              <TableCell>Categoría</TableCell>
              <TableCell>Texto que quiso enviar</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && !cargando && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No hay intentos que coincidan con los filtros.
                </TableCell>
              </TableRow>
            )}
            {items.map(m => (
              <TableRow key={m.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fechaHoraLocal(m.fecha)}</TableCell>
                <TableCell>
                  <Chip size="small" label={nombreSeveridad(m.severidad)}
                    color={colorSeveridad(m.severidad)} />
                </TableCell>
                <TableCell>
                  {m.bloqueado
                    ? <Chip size="small" label="Bloqueado" color="error" variant="outlined" />
                    : <Chip size="small" label="Solo alerta" variant="outlined" />}
                  {m.falsoPositivo && (
                    <Chip size="small" label="Falso positivo" sx={{ ml: 0.5 }}
                      color="info" variant="outlined" />
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  <DatosParticipante rol={m.autorRolChat} datos={m.remitente}
                    identity={m.usuarioIdentity} />
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  <DatosParticipante datos={m.destinatario} />
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  <div><b>{m.categoria || '—'}</b></div>
                  <div style={{ color: '#888', fontSize: 11 }}>
                    <code>{m.palabraDetectada || '—'}</code>
                  </div>
                </TableCell>
                <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal', fontSize: 12 }}>
                  {m.textoOriginal}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<EditNoteIcon />}
                    onClick={() => abrirNotas(m)}>
                    Notas
                  </Button>
                  {!m.falsoPositivo && (
                    <Button size="small" startIcon={<FlagIcon />}
                      onClick={() => marcarFalsoPositivo(m.id)}>
                      FP
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Dialog de notas */}
      <Dialog open={!!notasDialog} onClose={() => setNotasDialog(null)}
        fullWidth maxWidth="sm">
        <DialogTitle>Notas del validador</DialogTitle>
        <DialogContent>
          <TextField
            multiline rows={4} fullWidth autoFocus
            value={notasTexto}
            onChange={(e) => setNotasTexto(e.target.value)}
            placeholder="Contexto de la revisión, decisión tomada…"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotasDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarNotas}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
